import React, { useState, useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import InventoryTable from './components/InventoryTable';
import InventoryGridView from './components/InventoryGridView';
import InventoryForm from './pages/InventoryForm';
import ObjectDetails from './pages/ObjectDetails';
import ListsManager from './pages/ListsManager';
import Impressum from './pages/Impressum';
import Settings from './pages/Settings';
import Auth from './components/Auth';
import { supabase } from './lib/supabase';
import { CATEGORIES } from './lib/constants';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './components/common/SafeIcon';
import { ThemeProvider } from './context/ThemeContext';

const { FiMenu, FiList, FiGrid, FiLayers, FiLoader } = FiIcons;

const InventoryRoute = () => {
  const { category } = useParams();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('inventory_view_mode') || 'grid');

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { data: items } = await supabase.from('inventory').select('*').eq('category', category);
        setData(items || []);
      } catch (err) {
        console.error("Fetch Error:", err);
      } finally {
        setLoading(false);
      }
    };
    if (CATEGORIES[category]) fetchData();
  }, [category]);

  const handleDelete = async (id) => {
    if (window.confirm('Wirklich löschen?')) {
      await supabase.from('inventory').delete().eq('id', id);
      setData(prev => prev.filter(i => i.id !== id));
    }
  };

  if (!CATEGORIES[category]) return <Navigate to="/" />;
  const activeView = isMobile ? 'grid' : viewMode;

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h2 className="text-2xl sm:text-3xl font-black text-slate-800 dark:text-white flex items-center tracking-tight">
          <span className={`w-1.5 h-8 sm:h-10 mr-3 rounded-full ${CATEGORIES[category].color.split(' ')[0]}`}></span>
          {CATEGORIES[category].label}
        </h2>
        {!isMobile && (
          <div className="flex items-center bg-white dark:bg-slate-900 p-1 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800">
            <button onClick={() => { setViewMode('list'); localStorage.setItem('inventory_view_mode', 'list'); }} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all ${viewMode === 'list' ? 'bg-yellow-500 text-slate-900 shadow-md' : 'text-slate-500'}`} >
              <SafeIcon icon={FiList} /> Liste
            </button>
            <button onClick={() => { setViewMode('grid'); localStorage.setItem('inventory_view_mode', 'grid'); }} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all ${viewMode === 'grid' ? 'bg-yellow-500 text-slate-900 shadow-md' : 'text-slate-500'}`} >
              <SafeIcon icon={FiGrid} /> Karten
            </button>
          </div>
        )}
      </div>
      <div className="flex-1 overflow-hidden">
        {loading ? (
          <div className="h-full flex items-center justify-center text-slate-400 font-bold uppercase text-[10px] tracking-widest">
            Lade Daten...
          </div>
        ) : activeView === 'list' ? (
          <InventoryTable data={data} categoryKey={category} onDelete={handleDelete} />
        ) : (
          <InventoryGridView data={data} categoryKey={category} onDelete={handleDelete} />
        )}
      </div>
    </div>
  );
};

function AppContent() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <SafeIcon icon={FiLoader} className="text-4xl animate-spin text-yellow-500" />
      </div>
    );
  }

  if (!session) {
    return <Auth />;
  }

  return (
    <div className="flex h-full w-full bg-slate-50 dark:bg-slate-950 font-sans text-slate-900 dark:text-slate-100 transition-colors duration-300 overflow-hidden relative">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-[60] md:hidden transition-opacity duration-300" onClick={() => setSidebarOpen(false)} />
      )}
      <Sidebar isOpen={sidebarOpen} toggle={() => setSidebarOpen(!sidebarOpen)} />
      <div className="flex-1 flex flex-col min-w-0 h-full relative overflow-hidden">
        <header className="md:hidden flex-none bg-white dark:bg-slate-900 shadow-sm px-4 pt-[calc(env(safe-area-inset-top)+0.75rem)] pb-3 flex items-center justify-between z-50 border-b dark:border-slate-800">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center shadow-sm">
              <SafeIcon icon={FiLayers} className="text-slate-900 text-sm" />
            </div>
            <div>
              <div className="flex items-baseline gap-1">
                <h1 className="font-medium text-slate-800 dark:text-white tracking-tighter text-lg leading-none">MoBa Inventar</h1>
                <span className="text-[10px] font-light text-slate-400 dark:text-slate-500">v1.2.0</span>
              </div>
            </div>
          </div>
          <button onClick={() => setSidebarOpen(true)} className="p-2 bg-slate-100 dark:bg-slate-800 rounded-lg text-slate-600 dark:text-white active:bg-slate-200 transition-colors" >
            <SafeIcon icon={FiMenu} className="text-xl" />
          </button>
        </header>
        <main className="flex-1 overflow-y-auto px-4 py-6 md:p-10 scroll-smooth relative">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/inventory/:category/:id/details" element={<ObjectDetails />} />
            <Route path="/inventory/:category/:id" element={<InventoryForm />} />
            <Route path="/inventory/:category" element={<InventoryRoute />} />
            <Route path="/lists" element={<ListsManager />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/impressum" element={<Impressum />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <HashRouter>
        <AppContent />
      </HashRouter>
    </ThemeProvider>
  );
}

export default App;